import { getAuthHeaders, handleAuthError } from './auth';

// API URL configuration - automatically detects environment
const getApiUrl = () => {
  // Check if we're in production (deployed)
  if (window.location.hostname === 'ledgerly.spatium360.com') {
    return 'https://ledgerly.spatium360.com/api';
  }
  // Default to localhost for development
  return 'http://localhost:5050/api';
};

export const API_URL = getApiUrl();

/**
 * Create authenticated headers for API requests
 * @param {Object} additionalHeaders - Additional headers to include
 * @returns {Object} Headers object with authentication and content type
 */
const createHeaders = (additionalHeaders = {}) => {
  return {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
    ...additionalHeaders
  };
};

/**
 * Enhanced fetch wrapper with authentication and error handling
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise} Response data or throws error
 */
export const authenticatedFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...createHeaders(),
      ...options.headers
    }
  });

  // Handle authentication errors
  if (handleAuthError(response)) {
    throw new Error('Authentication failed');
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (e) {
      // If can't parse error response, use status text
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export async function fetchClients() {
  return authenticatedFetch(`${API_URL}/clients`);
}

export async function addClient(client) {
  // Support FormData for file uploads. Allow caller to pass FormData directly.
  if (typeof FormData !== 'undefined' && client instanceof FormData) {
    const response = await fetch(`${API_URL}/clients`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        // Let browser set multipart boundary automatically (no Content-Type)
      },
      body: client
    });
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try { const errorData = await response.json(); errorMessage = errorData.message || errorData.error || errorMessage; } catch { }
      throw new Error(errorMessage);
    }
    return response.json();
  }

  return authenticatedFetch(`${API_URL}/clients`, {
    method: 'POST',
    body: JSON.stringify(client),
  });
}

export async function deleteClient(id) {
  return authenticatedFetch(`${API_URL}/clients/${id}`, {
    method: 'DELETE',
  });
}

export async function updateClient(id, client) {
  // Support FormData (for document uploads)
  if (typeof FormData !== 'undefined' && client instanceof FormData) {
    const response = await fetch(`${API_URL}/clients/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        // No Content-Type here so browser sets multipart boundary
      },
      body: client
    });
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try { const errorData = await response.json(); errorMessage = errorData.message || errorData.error || errorMessage; } catch { }
      throw new Error(errorMessage);
    }
    return response.json();
  }
  return authenticatedFetch(`${API_URL}/clients/${id}`, {
    method: 'PUT',
    body: JSON.stringify(client),
  });
}

export async function fetchInvoices() {
  return authenticatedFetch(`${API_URL}/invoices`);
}

export async function fetchInvoiceById(id) {
  return authenticatedFetch(`${API_URL}/invoices/${id}`);
}

export async function addInvoice(invoice) {
  return authenticatedFetch(`${API_URL}/invoices`, {
    method: 'POST',
    body: JSON.stringify(invoice),
  });
}

export async function uploadPurchaseInvoiceFile(file) {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(`${API_URL}/invoices/upload-purchase-invoice`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
    },
    body: form
  });
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try { const err = await response.json(); errorMessage = err.message || err.error || errorMessage; } catch { }
    throw new Error(errorMessage);
  }
  return response.json();
}


export async function checkInvoiceExists(number, financialYear) {
  return authenticatedFetch(`${API_URL}/invoices/check-number/${number}/${financialYear}`);
}

export async function verifyPasscode(passcode) {
  return authenticatedFetch(`${API_URL}/settings/verify-passcode`, {
    method: 'POST',
    body: JSON.stringify({ passcode }),
  });
}

export async function deleteInvoice(id) {
  return authenticatedFetch(`${API_URL}/invoices/${id}`, {
    method: 'DELETE',
  });
}

export async function cancelInvoice(id, reason = '') {
  return authenticatedFetch(`${API_URL}/invoices/${id}/cancel`, {
    method: 'PATCH',
    body: JSON.stringify({ reason })
  });
}

export async function uncancelInvoice(id) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/uncancel`, {
    method: 'PATCH'
  });
}

// Invoice approval workflow
export async function submitInvoiceForReview(id, payload = {}) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/submit`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function verifyInvoice(id, payload = {}) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/verify`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function approveInvoice(id, payload = {}) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function rejectInvoice(id, payload = {}) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function assignInvoice(id, userId) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/assign`, {
    method: 'PATCH',
    body: JSON.stringify({ userId }),
  });
}

// Configuration API functions
export async function fetchConfig() {
  return authenticatedFetch(`${API_URL}/settings/config`);
}

export async function fetchInvoiceConfig() {
  return authenticatedFetch(`${API_URL}/settings/config/invoice`);
}

export async function updateInvoiceNumber(nextInvoiceNumber) {
  return authenticatedFetch(`${API_URL}/settings/config/invoice-number`, {
    method: 'PATCH',
    body: JSON.stringify({ nextInvoiceNumber }),
  });
}

export async function updateQuoteNumber(nextQuoteNumber) {
  return authenticatedFetch(`${API_URL}/settings/config/quote-number`, {
    method: 'PATCH',
    body: JSON.stringify({ nextQuoteNumber }),
  });
}

export async function updateCreditNoteNumber(nextCreditNoteNumber) {
  return authenticatedFetch(`${API_URL}/settings/config/credit-note-number`, {
    method: 'PATCH',
    body: JSON.stringify({ nextCreditNoteNumber }),
  });
}

export async function getNextTermSheetNumber() {
  return authenticatedFetch(`${API_URL}/settings/config/next-term-sheet-number`);
}

export async function sendInvoiceEmail(invoiceId, emailData) {
  return authenticatedFetch(`${API_URL}/invoices/${invoiceId}/send-email`, {
    method: 'POST',
    body: JSON.stringify(emailData),
  });
}

export async function downloadInvoicePDF(invoiceId) {
  const response = await fetch(`${API_URL}/invoices/${invoiceId}/pdf`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (e) {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.blob();
}

// Quotations API
export async function fetchQuotations() {
  return authenticatedFetch(`${API_URL}/quotations`);
}

export async function addQuotation(data) {
  return authenticatedFetch(`${API_URL}/quotations`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updateQuotation(id, data) {
  return authenticatedFetch(`${API_URL}/quotations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deleteQuotation(id) {
  return authenticatedFetch(`${API_URL}/quotations/${id}`, {
    method: 'DELETE'
  });
}

export async function downloadQuotationPDF(id) {
  const response = await fetch(`${API_URL}/quotations/${id}/pdf`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try { const errorData = await response.json(); errorMessage = errorData.message || errorData.error || errorMessage; } catch { }
    throw new Error(errorMessage);
  }
  return response.blob();
}

// New quotation versioning and status management functions
export async function createQuotationVersion(id, data) {
  return authenticatedFetch(`${API_URL}/quotations/${id}/version`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updateQuotationStatus(id, status) {
  return authenticatedFetch(`${API_URL}/quotations/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status })
  });
}

export async function getQuotationVersions(id) {
  return authenticatedFetch(`${API_URL}/quotations/${id}/versions`);
}

export async function saveConfig(config) {
  return authenticatedFetch(`${API_URL}/settings/config`, {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

export async function uploadTermSheetSignature(file) {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(`${API_URL}/settings/config/term-sheet-signature`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      // Do not set Content-Type; browser will set multipart/form-data with boundary
    },
    body: form
  });
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try { const err = await response.json(); errorMessage = err.message || err.error || errorMessage; } catch { }
    throw new Error(errorMessage);
  }
  return response.json();
}

// Facilities API
export async function fetchFacilities() {
  return authenticatedFetch(`${API_URL}/settings/facilities`);
}

export async function fetchFacility(id) {
  return authenticatedFetch(`${API_URL}/facilities/${id}`);
}

export async function addFacility(name) {
  return authenticatedFetch(`${API_URL}/settings/facilities`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function updateFacility(id, name) {
  return authenticatedFetch(`${API_URL}/settings/facilities/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
}

export async function deleteFacility(id) {
  return authenticatedFetch(`${API_URL}/settings/facilities/${id}`, {
    method: 'DELETE',
  });
}

// Invoice Categories API (stored in settings)
export async function fetchInvoiceCategories() {
  return authenticatedFetch(`${API_URL}/settings/invoice-categories`);
}

export async function addInvoiceCategory(name) {
  return authenticatedFetch(`${API_URL}/settings/invoice-categories`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function updateInvoiceCategory(id, name) {
  return authenticatedFetch(`${API_URL}/settings/invoice-categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
}

export async function deleteInvoiceCategory(id) {
  return authenticatedFetch(`${API_URL}/settings/invoice-categories/${id}`, {
    method: 'DELETE',
  });
}

// Departments API (stored in settings)
export async function fetchDepartments() {
  return authenticatedFetch(`${API_URL}/settings/departments`);
}

export async function addDepartment(name) {
  return authenticatedFetch(`${API_URL}/settings/departments`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function updateDepartment(id, name) {
  return authenticatedFetch(`${API_URL}/settings/departments/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
}

export async function deleteDepartment(id) {
  return authenticatedFetch(`${API_URL}/settings/departments/${id}`, {
    method: 'DELETE',
  });
}

// Invoice Rules API (stored in settings)
export async function fetchInvoiceRules() {
  return authenticatedFetch(`${API_URL}/settings/invoice-rules`);
}

export async function addInvoiceRule(ruleData) {
  return authenticatedFetch(`${API_URL}/settings/invoice-rules`, {
    method: 'POST',
    body: JSON.stringify(ruleData),
  });
}

export async function updateInvoiceRule(id, ruleData) {
  return authenticatedFetch(`${API_URL}/settings/invoice-rules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(ruleData),
  });
}

export async function deleteInvoiceRule(id) {
  return authenticatedFetch(`${API_URL}/settings/invoice-rules/${id}`, {
    method: 'DELETE',
  });
}

// Invoice Types API (stored in settings)
export async function fetchInvoiceTypes() {
  return authenticatedFetch(`${API_URL}/settings/invoice-types`);
}

export async function addInvoiceType(name) {
  return authenticatedFetch(`${API_URL}/settings/invoice-types`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function updateInvoiceType(id, name) {
  return authenticatedFetch(`${API_URL}/settings/invoice-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
}

export async function deleteInvoiceType(id) {
  return authenticatedFetch(`${API_URL}/settings/invoice-types/${id}`, {
    method: 'DELETE',
  });
}

export async function updateInvoice(id, invoice) {
  return authenticatedFetch(`${API_URL}/invoices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(invoice),
  });
}

export async function updateInvoicePayment(id, payment) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/payment`, {
    method: 'PATCH',
    body: JSON.stringify(payment),
  });
}

export async function addInvoicePayment(id, payment) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/payments`, {
    method: 'POST',
    body: JSON.stringify(payment),
  });
}

export async function updateInvoicePaymentItem(id, paymentId, payment) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/payments/${paymentId}`, {
    method: 'PUT',
    body: JSON.stringify(payment),
  });
}

export async function deleteInvoicePaymentItem(id, paymentId) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/payments/${paymentId}`, {
    method: 'DELETE',
  });
}

// Credit Note API functions
export async function getNextCreditNoteNumber() {
  return authenticatedFetch(`${API_URL}/invoices/next-credit-note-number`);
}

export async function createCreditNote(creditNoteData) {
  return authenticatedFetch(`${API_URL}/invoices/credit-note`, {
    method: 'POST',
    body: JSON.stringify(creditNoteData),
  });
}

export async function createStandaloneCreditNote(data) {
  return authenticatedFetch(`${API_URL}/invoices/credit-note/standalone`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function getCreditNotesForInvoice(invoiceId) {
  return authenticatedFetch(`${API_URL}/invoices/${invoiceId}/credit-notes`);
}

export async function getAllCreditNotes() {
  return authenticatedFetch(`${API_URL}/invoices/credit-notes/all`);
}

// Fetch unapplied credit notes for a client and type
export async function getClientCreditNotes(clientId, type) {
  return authenticatedFetch(`${API_URL}/invoices/credit-notes/all`).then(list => {
    return list.filter(n => n.client?._id === clientId && n.invoiceType === 'credit_note' && !n.creditApplied);
  });
}

export async function markCreditNotesApplied(noteIds) {
  return authenticatedFetch(`${API_URL}/invoices/credit-notes/mark-applied`, {
    method: 'POST',
    body: JSON.stringify({ ids: noteIds })
  });
}

// New API functions for edit restrictions
export async function fetchInvoicesWithEditStatus() {
  console.log(`[API DEBUG] Starting fetchInvoicesWithEditStatus() - calling ${API_URL}/invoices/edit-status`);
  try {
    const response = await authenticatedFetch(`${API_URL}/invoices/edit-status`);
    console.log(`[API DEBUG] Raw response received from /invoices/edit-status:`, {
      isArray: Array.isArray(response),
      count: Array.isArray(response) ? response.length : 'not an array',
      type: typeof response,
      invoices: Array.isArray(response) ? response.map(inv => ({
        id: inv._id || inv.id,
        invoiceNumber: inv.invoiceNumber,
        invoiceNumberType: typeof inv.invoiceNumber,
        allKeys: Object.keys(inv).slice(0, 10) // First 10 keys
      })) : 'not an array'
    });

    if (!Array.isArray(response)) {
      console.error('[API DEBUG] ERROR: Response is not an array!', response);
      return [];
    }

    // CRITICAL: Filter out any non-Invoice objects (e.g., User objects that might have slipped in)
    const validInvoices = response.filter(inv => {
      // Check if this is actually an Invoice object (not a User or other object)
      const hasUserFields = inv.moduleAccess !== undefined ||
        inv.email !== undefined ||
        (inv.role !== undefined && !inv.invoiceNumber);

      const hasInvoiceFields = inv.invoiceNumber !== undefined ||
        inv.invoiceDate !== undefined ||
        inv.items !== undefined ||
        inv.totals !== undefined;

      // If it has User fields but no Invoice fields, it's not an Invoice
      if (hasUserFields && !hasInvoiceFields) {
        console.error('[API DEBUG] Filtering out non-Invoice object:', {
          id: inv._id || inv.id,
          hasUserFields,
          hasInvoiceFields,
          keys: Object.keys(inv).slice(0, 10)
        });
        return false;
      }

      return true;
    });

    if (validInvoices.length !== response.length) {
      console.warn(`[API DEBUG] Filtered out ${response.length - validInvoices.length} non-Invoice objects from response`);
    }

    console.log(`[API DEBUG] Returning ${validInvoices.length} valid invoices (out of ${response.length} total)`);
    return validInvoices;
  } catch (error) {
    console.error('[API DEBUG] ERROR in fetchInvoicesWithEditStatus:', error);
    throw error;
  }
}

export async function getInvoiceEditStatus(id) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/edit-status`);
}

export async function migrateEditDeadlines() {
  return authenticatedFetch(`${API_URL}/invoices/migrate-edit-deadlines`, {
    method: 'POST',
  });
}

// User Management API functions
export async function fetchUsers() {
  return authenticatedFetch(`${API_URL}/users`);
}

export async function addUser(userData) {
  return authenticatedFetch(`${API_URL}/users`, {
    method: 'POST',
    body: JSON.stringify(userData),
  });
}

export async function updateUser(userId, userData) {
  return authenticatedFetch(`${API_URL}/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  });
}

export async function deleteUser(userId) {
  return authenticatedFetch(`${API_URL}/users/${userId}`, {
    method: 'DELETE',
  });
}

export async function changePassword(currentPassword, newPassword) {
  return authenticatedFetch(`${API_URL}/users/change-password`, {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function fetchUserMetrics() {
  return authenticatedFetch(`${API_URL}/users/metrics`);
}

// Initialize admin user (public endpoint)
export async function initializeAdmin() {
  const API_URL_BASE = getApiUrl();
  const response = await fetch(`${API_URL_BASE}/users/init-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to initialize admin user');
  }

  return response.json();
}

// Expenses API
export async function fetchExpenses(filters = {}) {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const url = queryParams.toString() ? `${API_URL}/expenses?${queryParams.toString()}` : `${API_URL}/expenses`;
  return authenticatedFetch(url);
}

export async function addExpense(expense) {
  return authenticatedFetch(`${API_URL}/expenses`, {
    method: 'POST',
    body: JSON.stringify(expense),
  });
}

export async function updateExpense(id, expense) {
  return authenticatedFetch(`${API_URL}/expenses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(expense),
  });
}

export async function deleteExpense(id) {
  return authenticatedFetch(`${API_URL}/expenses/${id}`, {
    method: 'DELETE',
  });
}

export async function fetchExpenseCategories() {
  return authenticatedFetch(`${API_URL}/expenses/categories/list`);
}

export async function fetchExpenseStats(filters = {}) {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const url = queryParams.toString() ? `${API_URL}/expenses/stats/summary?${queryParams.toString()}` : `${API_URL}/expenses/stats/summary`;
  return authenticatedFetch(url);
}

// Expense Categories Management API
export async function fetchExpenseCategoriesList() {
  return authenticatedFetch(`${API_URL}/expense-categories`);
}

export async function addExpenseCategory(category) {
  return authenticatedFetch(`${API_URL}/expense-categories`, {
    method: 'POST',
    body: JSON.stringify({ name: category, description: '' }),
  });
}

export async function updateExpenseCategory(id, category) {
  return authenticatedFetch(`${API_URL}/expense-categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name: category, description: '' }),
  });
}

export async function deleteExpenseCategory(id) {
  return authenticatedFetch(`${API_URL}/expense-categories/${id}`, {
    method: 'DELETE',
  });
}

export async function fetchExpenseCategoriesWithUsage() {
  return authenticatedFetch(`${API_URL}/expense-categories/stats/usage`);
}

// Log timeline event
export async function logInvoiceEvent(id, type, details) {
  return authenticatedFetch(`${API_URL}/invoices/${id}/log-event`, {
    method: 'POST',
    body: JSON.stringify({ type, details })
  });
}

// Vendors API
export async function fetchVendors(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') query.append(k, v);
  });
  const url = query.toString() ? `${API_URL}/vendors?${query.toString()}` : `${API_URL}/vendors`;
  return authenticatedFetch(url);
}

export async function addVendor(vendor) {
  return authenticatedFetch(`${API_URL}/vendors`, {
    method: 'POST',
    body: JSON.stringify(vendor),
  });
}

export async function updateVendor(id, vendor) {
  return authenticatedFetch(`${API_URL}/vendors/${id}`, {
    method: 'PUT',
    body: JSON.stringify(vendor),
  });
}

export async function deleteVendor(id) {
  return authenticatedFetch(`${API_URL}/vendors/${id}`, {
    method: 'DELETE',
  });
}

export async function bulkAddVendors(vendors) {
  return authenticatedFetch(`${API_URL}/vendors/bulk`, {
    method: 'POST',
    body: JSON.stringify({ vendors })
  });
}

export async function fetchVendorById(id) {
  return authenticatedFetch(`${API_URL}/vendors/${id}`);
}

// Payables API
export async function fetchPayables(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') query.append(k, v);
  });
  const url = query.toString() ? `${API_URL}/payables?${query.toString()}` : `${API_URL}/payables`;
  return authenticatedFetch(url);
}

export async function fetchPayablesStats() {
  return authenticatedFetch(`${API_URL}/payables/stats/summary`);
}

export async function addPayable(payable) {
  return authenticatedFetch(`${API_URL}/payables`, {
    method: 'POST',
    body: JSON.stringify(payable),
  });
}

export async function updatePayable(id, payable) {
  return authenticatedFetch(`${API_URL}/payables/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payable),
  });
}

export async function deletePayable(id) {
  return authenticatedFetch(`${API_URL}/payables/${id}`, {
    method: 'DELETE',
  });
}

export async function addPayablePayment(id, payment) {
  return authenticatedFetch(`${API_URL}/payables/${id}/payments`, {
    method: 'POST',
    body: JSON.stringify(payment),
  });
}

export async function fetchPayableById(id) {
  return authenticatedFetch(`${API_URL}/payables/${id}`);
}


// TermSheet API functions
export const fetchTermSheets = async () => {
  return authenticatedFetch(`${API_URL}/term-sheets`);
};

export const fetchTermSheet = async (id) => {
  return authenticatedFetch(`${API_URL}/term-sheets/${id}`);
};

export const addTermSheet = async (termSheetData) => {
  return authenticatedFetch(`${API_URL}/term-sheets`, {
    method: 'POST',
    body: JSON.stringify(termSheetData)
  });
};

export const updateTermSheet = async (id, termSheetData) => {
  return authenticatedFetch(`${API_URL}/term-sheets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(termSheetData)
  });
};

export const deleteTermSheet = async (id) => {
  return authenticatedFetch(`${API_URL}/term-sheets/${id}`, {
    method: 'DELETE'
  });
};

export const generateTermSheetPDF = async (id) => {
  const response = await fetch(`${API_URL}/term-sheets/${id}/generate-pdf`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  if (handleAuthError(response)) {
    throw new Error('Authentication failed');
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (e) {
      // Use default error message
    }
    throw new Error(errorMessage);
  }

  return response.blob();
};

// Leads API functions
export async function fetchLeads() {
  return authenticatedFetch(`${API_URL}/leads`);
}

export async function addLead(data) {
  return authenticatedFetch(`${API_URL}/leads`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updateLead(id, data) {
  return authenticatedFetch(`${API_URL}/leads/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deleteLead(id) {
  return authenticatedFetch(`${API_URL}/leads/${id}`, {
    method: 'DELETE'
  });
}

export async function updateLeadStatus(id, status) {
  return authenticatedFetch(`${API_URL}/leads/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status })
  });
}

export async function generateTermSheetFromLead(leadId) {
  return authenticatedFetch(`${API_URL}/leads/${leadId}/generate-term-sheet`, {
    method: 'POST'
  });
}

export async function fetchPerformanceSummary(filters = {}) {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const url = queryParams.toString() ? `${API_URL}/reports/performance-summary?${queryParams.toString()}` : `${API_URL}/reports/performance-summary`;
  return authenticatedFetch(url);
}

